import * as React from 'react';
import { Button, Dialog, ScopedCssBaseline, Stack, ThemeProvider, lanekassenTheme } from '@lanekassen/ui';
import ReactDOM from 'react-dom/client';
import DOMPurify from 'isomorphic-dompurify';
import { hentTekster, refreshSession } from './api';
import type { ModalTeksterType } from './types';

interface InaktivitetsModalProps {
  container: HTMLElement;
  tekster: ModalTeksterType;
  erInaktivitetsVariant: boolean;
}

export function InaktivitetsModal({ container, tekster, erInaktivitetsVariant }: InaktivitetsModalProps) {
  const [modalState, setModalState] = React.useState({ inaktivitet: erInaktivitetsVariant, isOpen: true });
  const utvidSesjon = () => {
    refreshSession()
      .then((res) => {
        if (!res.ok) {
          // Noe gikk galt tvinger refresh
          window.location.reload();
        } else {
          setModalState((prev) => ({ ...prev, isOpen: false }));
        }
      })
      .catch(() => window.location.reload());
  };

  React.useEffect(() => {
    const onVis = (e: CustomEvent) =>
      setModalState((prev) => ({ ...prev, inaktivitet: e.detail.erInaktivitetsVariant, isOpen: true }));
    container.addEventListener('visInaktivitetsModal', onVis as EventListener);
    return () => container.removeEventListener('visInaktivitetsModal', onVis as EventListener);
  }, [container]);

  const handleOnClose = () => {
    // Vi kan ikke utvide cookie duration dersom sesjon utløper
    if (modalState.inaktivitet) {
      utvidSesjon();
    }
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };
  const innholdsTekst = DOMPurify.sanitize(
    modalState.inaktivitet ? tekster.innholdsTekst : tekster.sesjonUtloperInnholdsTekst,
  );
  const tittel = modalState.inaktivitet ? tekster.tittel : tekster.sesjonUtloperTittel;

  return (
    <Dialog isOpen={modalState.isOpen} onClose={handleOnClose}>
      <Dialog.Header
        onClose={handleOnClose}
        closeButtonLabel={tekster.cultureName.toLowerCase() === 'en-us' ? 'Close' : 'Lukk'}
      >
        {tittel}
      </Dialog.Header>
      <Dialog.Content dangerouslySetInnerHTML={{ __html: innholdsTekst }} />
      <Dialog.Footer>
        <Stack direction="row" spacing={2}>
          <Button variant="solid" onClick={handleOnClose} href="/du/logger_ut">
            {tekster.loggUtKnappTekst}
          </Button>
          {modalState.inaktivitet ? (
            <Button autoFocus onClick={utvidSesjon}>
              {tekster.utvidSesjonsTekst}
            </Button>
          ) : (
            <Button autoFocus href="/du/logger_inn">
              {tekster.fornySesjonsTekst}
            </Button>
          )}
        </Stack>
      </Dialog.Footer>
    </Dialog>
  );
}

export function lagOgVisInaktivitetsModal({
  erInaktivitetsVariant,
}: Pick<InaktivitetsModalProps, 'erInaktivitetsVariant'>) {
  const body = document.getElementsByTagName('body')[0];
  let dialogContainer = document.getElementById('dialogContainer');
  if (dialogContainer == null) {
    dialogContainer = document.createElement('div');
    dialogContainer.id = 'dialogContainer';
    body.appendChild(dialogContainer);
    const root = ReactDOM.createRoot(dialogContainer);
    hentTekster().then((tekster) => {
      root.render(
        <React.StrictMode>
          <ThemeProvider theme={lanekassenTheme} defaultMode="light" storageManager={null}>
            <ScopedCssBaseline>
              <InaktivitetsModal
                container={dialogContainer!}
                tekster={tekster}
                erInaktivitetsVariant={erInaktivitetsVariant}
              />
            </ScopedCssBaseline>
          </ThemeProvider>
        </React.StrictMode>,
      );
    });
  } else {
    const event = new CustomEvent('visInaktivitetsModal', {
      detail: {
        erInaktivitetsVariant,
      },
    });
    dialogContainer.dispatchEvent(event);
  }
}
